import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { activityAnalyticsApi } from "../../../api";
import { queryKeys } from "../../../queryKeys";
import { buildActivityAnalyticsView } from "../../../utils/activityAnalyticsView";
import { DataTable } from "./DataTable";
import { MetricGrid } from "./MetricGrid";

type Props = { eventId: string; token: string };

/**
 * Sales, revenue, fill rate and check-ins per activity and slot for events sold by activity on
 * zoniq.in. Analysis only: activities are created in the ticketing dashboard. Renders nothing
 * for events with no activity sales.
 */
export const ActivityAnalyticsPanel: React.FC<Props> = ({ eventId, token }) => {
  const [showAllSlots, setShowAllSlots] = useState(false);
  const query = useQuery({
    queryKey: queryKeys.activityAnalytics(eventId),
    queryFn: () => activityAnalyticsApi.get(eventId, token),
    staleTime: 30_000,
  });

  if (query.isLoading || query.isError || !query.data) return null;
  const view = buildActivityAnalyticsView(query.data);
  if (!view.hasSales) return null;

  return (
    <>
      <div className="eventos-card">
        <div className="eventos-label">Activity sales</div>
        <div className="eventos-subtle">
          Each ticket has one QR but several activity slots; revenue is the ticket price split across its activities.
        </div>
      </div>
      <MetricGrid metrics={view.metrics} cols={4} />
      <div className="eventos-card">
        <div className="eventos-label">By activity</div>
        <DataTable table={view.activityTable} emptyLabel="No activity sales yet." />
      </div>
      <div className="eventos-card">
        <div className="eventos-label">Most popular slots</div>
        <DataTable table={view.popularTable} emptyLabel="No slot sales yet." />
      </div>
      <div className="eventos-card">
        <div className="eventos-label" style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span>Every slot</span>
          <button type="button" className="eventos-nav-btn" onClick={() => setShowAllSlots((v) => !v)}>
            {showAllSlots ? "Hide" : `Show ${query.data.by_slot.length}`}
          </button>
        </div>
        {showAllSlots ? <DataTable table={view.slotTable} emptyLabel="No slot sales yet." /> : null}
      </div>
    </>
  );
};
